const UPDATE_EVENT = 'app-update-available';
const VERSION_PATH = '/version.json';
const CHECK_INTERVAL_MS = 60 * 1000;
const REGISTRATION_UPDATE_MS = 2 * 60 * 1000;

let serviceWorkerRegistration = null;
let currentVersion = null;
let versionCheckTimer = null;
let swUpdateTimer = null;
let controllerChangeBound = false;
let promptAlreadyOpen = false;

function runRegistrationUpdate() {
  if (!serviceWorkerRegistration || typeof serviceWorkerRegistration.update !== 'function') {
    return;
  }

  serviceWorkerRegistration.update().catch(() => null);
}

function dispatchUpdate(detail = {}) {
  if (promptAlreadyOpen) return;
  promptAlreadyOpen = true;
  window.dispatchEvent(new CustomEvent(UPDATE_EVENT, { detail }));
}

async function fetchVersionManifest() {
  const response = await fetch(`${VERSION_PATH}?t=${Date.now()}`, {
    cache: 'no-store',
    headers: {
      'cache-control': 'no-cache',
      pragma: 'no-cache',
    },
  });

  if (!response.ok) {
    throw new Error(`Version manifest request failed with ${response.status}`);
  }

  return response.json();
}

async function checkVersionManifest() {
  try {
    const payload = await fetchVersionManifest();
    const nextVersion = payload?.version || payload?.buildAt || null;
    if (!nextVersion) return;

    if (!currentVersion) {
      currentVersion = nextVersion;
      return;
    }

    if (nextVersion !== currentVersion) {
      currentVersion = nextVersion;
      dispatchUpdate({
        source: 'version-manifest',
        version: nextVersion,
      });
    }
  } catch (_error) {
    // noop
  }
}

function watchWaitingServiceWorker(registration) {
  if (!registration) return;

  if (registration.waiting) {
    dispatchUpdate({
      source: 'service-worker',
      registration,
    });
    return;
  }

  registration.addEventListener('updatefound', () => {
    const installingWorker = registration.installing;
    if (!installingWorker) return;

    installingWorker.addEventListener('statechange', () => {
      if (installingWorker.state === 'installed' && navigator.serviceWorker.controller) {
        dispatchUpdate({
          source: 'service-worker',
          registration,
        });
      }
    });
  });
}

function bindControllerChangeReload() {
  if (controllerChangeBound || !navigator.serviceWorker) return;

  controllerChangeBound = true;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    window.location.reload();
  });
}

function startVersionWatchers() {
  checkVersionManifest();

  if (!versionCheckTimer) {
    versionCheckTimer = window.setInterval(checkVersionManifest, CHECK_INTERVAL_MS);
  }

  const onVisible = () => {
    if (document.visibilityState === 'visible') {
      checkVersionManifest();
      runRegistrationUpdate();
    }
  };

  const onOnline = () => {
    checkVersionManifest();
    runRegistrationUpdate();
  };

  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('focus', onVisible);
  window.addEventListener('online', onOnline);
}

export function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    checkVersionManifest().catch(() => null);
    return;
  }

  window.addEventListener('load', async () => {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
      serviceWorkerRegistration = registration;
      bindControllerChangeReload();
      watchWaitingServiceWorker(registration);
      runRegistrationUpdate();

      if (!swUpdateTimer) {
        swUpdateTimer = window.setInterval(runRegistrationUpdate, REGISTRATION_UPDATE_MS);
      }
    } catch (_error) {
      // noop
    }

    startVersionWatchers();
  });
}

export function applyAppUpdate(registration = null) {
  const targetRegistration = registration || serviceWorkerRegistration;

  if (targetRegistration?.waiting) {
    targetRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
    return;
  }

  window.location.reload();
}

export { UPDATE_EVENT };
